import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { useApi, getErrorMessage } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  AlertTriangle, AlertCircle, CheckCircle2, ChevronRight, Building2,
  RefreshCw, Search, X, Wrench
} from 'lucide-react';

// ---------- helpers ----------
function relativeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} jours`;
  if (days < 30) return `il y a ${Math.floor(days / 7)} sem`;
  if (days < 365) return `il y a ${Math.floor(days / 30)} mois`;
  return d.toLocaleDateString('fr-FR');
}

function exactDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

const SOURCE_LABELS = {
  conversation: { label: 'Conversation', color: 'text-blue-600' },
  comment: { label: 'Avis public', color: 'text-purple-600' },
  private_remark: { label: 'Avis privé', color: 'text-orange-600' },
  cleaning_report: { label: 'Ménage', color: 'text-teal-600' },
};

const STATUS_LABELS = {
  nouveau: { label: 'Nouveau', color: 'text-[#C73E51]', dot: '#FF5A6E' },
  en_cours: { label: 'En cours', color: 'text-amber-600', dot: '#F59E0B' },
  resolu: { label: 'Résolu', color: 'text-emerald-600', dot: '#10B981' },
};

// ---------- Timeline sparkline ----------
const TimelineWidget = ({ data }) => {
  if (!data?.length) return null;
  const max = Math.max(1, ...data.map(d => d.total));
  const totalSum = data.reduce((s, d) => s + d.total, 0);
  const majorSum = data.reduce((s, d) => s + d.major, 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)] p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Problèmes par jour (30 derniers jours)</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {totalSum} signalement{totalSum > 1 ? 's' : ''} · {majorSum} majeur{majorSum > 1 ? 's' : ''}
          </p>
        </div>
      </div>
      <div className="flex items-end gap-[2px] h-16">
        {data.map((d, i) => {
          const h = (d.total / max) * 100;
          const majorH = (d.major / max) * 100;
          return (
            <div
              key={d.date}
              className="flex-1 relative group cursor-pointer"
              style={{ height: '100%' }}
            >
              {/* Barre totale (slate) */}
              <div
                className="absolute bottom-0 left-0 right-0 bg-slate-200 group-hover:bg-slate-300 rounded-sm transition-colors"
                style={{ height: `${h}%`, minHeight: d.total > 0 ? '2px' : 0 }}
              />
              {/* Sur-couche majeurs (rouge) */}
              {d.major > 0 && (
                <div
                  className="absolute bottom-0 left-0 right-0 bg-[#FF5A6E] rounded-sm"
                  style={{ height: `${majorH}%`, minHeight: '2px' }}
                />
              )}
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-10">
                {new Date(d.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                {' · '}
                {d.total} total{d.major > 0 ? `, ${d.major} majeur${d.major > 1 ? 's' : ''}` : ''}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] text-slate-400">
        <span>{new Date(data[0]?.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-300" /> Total</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#FF5A6E]" /> Majeurs</span>
        </span>
        <span>Aujourd'hui</span>
      </div>
    </div>
  );
};

export const IssuesPage = () => {
  const api = useApi();
  const [issues, setIssues] = useState([]);
  const [issueCount, setIssueCount] = useState({});
  const [categories, setCategories] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [sourceTab, setSourceTab] = useState('all');
  const [filters, setFilters] = useState({ severity: '', status: '', category: '' });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.severity) params.set('severity', filters.severity);
      if (filters.status) params.set('status', filters.status);
      if (filters.category) params.set('category', filters.category);
      if (sourceTab === 'conversation') params.set('source', 'conversation');
      if (sourceTab === 'reviews') params.set('source', 'reviews');
      if (sourceTab === 'cleaning') params.set('source', 'cleaning');
      if (search) params.set('search', search);

      const [issuesRes, countRes, catsRes, tlRes] = await Promise.all([
        api.get(`/guest-comments/issues?${params.toString()}`),
        api.get('/guest-comments/issues/count'),
        api.get('/guest-comments/issues/categories'),
        api.get('/guest-comments/issues/timeline?days=30').catch(() => ({ data: { series: [] } })),
      ]);
      setIssues(issuesRes.data);
      setIssueCount(countRes.data);
      setCategories(catsRes.data);
      setTimeline(tlRes.data?.series || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du chargement'));
    } finally {
      setLoading(false);
    }
  }, [api, filters, sourceTab, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Keyboard shortcut "/" to focus search
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        document.querySelector('[data-testid="search-issues"]')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleAnalyzeConversations = async () => {
    setAnalyzing(true);
    try {
      await api.post('/quality/analyze-conversations');
      toast.success('Analyse des conversations lancée. Les résultats apparaîtront sous peu.');
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du lancement de l'analyse"));
    } finally {
      setAnalyzing(false);
    }
  };

  const tabs = useMemo(() => ([
    { key: 'all', label: 'Tout', count: issueCount.total_issues || 0 },
    { key: 'conversation', label: 'Conversations', count: issueCount.from_conversations || 0 },
    { key: 'reviews', label: 'Avis voyageurs', count: (issueCount.from_comments || 0) + (issueCount.from_private_remarks || 0) },
    { key: 'cleaning', label: 'Ménage', count: issueCount.from_cleaning_reports || 0 },
  ]), [issueCount]);

  if (loading) {
    return (
      <MainLayout>
        <Header title="Alertes & Problèmes" />
        <div className="p-6 flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-slate-400">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Header title="Alertes & Problèmes" subtitle="Suivi qualité : conversations Beds24, avis voyageurs et retours ménage" />

      <div className="p-6 space-y-5 animate-fadeIn" data-testid="issues-page">

        {/* ===== Summary : 2 cards alerte + 1 stats ===== */}
        <div className="flex items-stretch gap-3 flex-wrap">
          {/* Card 1 : Nouveaux */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 flex-1 min-w-[200px] shadow-[0_2px_8px_rgba(0,0,0,0.03)] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-[#FF5A6E]" />
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-[#C73E51] uppercase tracking-wide">Nouveaux à traiter</span>
              {(issueCount.new_issues || 0) > 0 && (
                <span className="relative flex">
                  <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-[#FF5A6E] opacity-50 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF5A6E]" />
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-slate-900">{issueCount.new_issues || 0}</span>
            </div>
          </div>

          {/* Card 2 : Majeurs actifs */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 flex-1 min-w-[200px] shadow-[0_2px_8px_rgba(0,0,0,0.03)] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" />
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Majeurs actifs</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-slate-900">{issueCount.major_issues || 0}</span>
              <span className="text-xs text-slate-400">non résolus</span>
            </div>
          </div>

          {/* Stats sources */}
          <div className="bg-slate-50 rounded-2xl border border-slate-100 px-5 py-4 flex flex-col justify-center w-72">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-2">Sources</div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Conversations Beds24</span>
                <span className="font-semibold text-slate-900">{issueCount.from_conversations || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Avis voyageurs</span>
                <span className="font-semibold text-slate-900">{(issueCount.from_comments || 0) + (issueCount.from_private_remarks || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Ménage</span>
                <span className="font-semibold text-slate-900">{issueCount.from_cleaning_reports || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Timeline 30 jours ===== */}
        <TimelineWidget data={timeline} />

        {/* ===== Toolbar : tabs + search + analyser ===== */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Source tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 flex-wrap">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => { setSourceTab(tab.key); setFilters(f => ({ ...f, category: '' })); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  sourceTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`tab-${tab.key}`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-md min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Rechercher un voyageur, un logement, un mot-clé..."
              className="w-full pl-10 pr-12 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF5A6E]/20 focus:border-[#FF5A6E] focus:bg-white"
              data-testid="search-issues"
            />
            {searchInput ? (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Effacer la recherche"
              >
                <X size={14} />
              </button>
            ) : (
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 bg-white border border-slate-200 px-1.5 py-0.5 rounded font-mono pointer-events-none">/</kbd>
            )}
          </div>

          <Button
            size="sm"
            onClick={handleAnalyzeConversations}
            disabled={analyzing}
            className="bg-[#FF5A6E] hover:bg-[#E84B5F] text-white"
            data-testid="analyze-conversations-btn"
          >
            <RefreshCw size={14} className={`mr-2 ${analyzing ? 'animate-spin' : ''}`} />
            {analyzing ? 'Analyse...' : 'Analyser les conversations'}
          </Button>
        </div>

        {/* ===== Category chips + filters ===== */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap flex-1">
            <button
              onClick={() => setFilters(f => ({ ...f, category: '' }))}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${!filters.category ? 'bg-[#FF5A6E] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
              Toutes <span className={`ml-0.5 ${!filters.category ? 'opacity-70' : 'text-slate-400'}`}>{issueCount.total_issues || 0}</span>
            </button>
            {categories.map(cat => (
              <button
                key={cat.category}
                onClick={() => setFilters(f => ({ ...f, category: f.category === cat.category ? '' : cat.category }))}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors capitalize ${filters.category === cat.category ? 'bg-[#FF5A6E] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                {cat.category} <span className={`ml-0.5 ${filters.category === cat.category ? 'opacity-70' : 'text-slate-400'}`}>{cat.count}</span>
              </button>
            ))}
          </div>

          <div className="flex gap-2 flex-shrink-0">
            <select value={filters.severity} onChange={e => setFilters(f => ({ ...f, severity: e.target.value }))}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600">
              <option value="">Sévérité : toutes</option>
              <option value="probleme_majeur">Majeur</option>
              <option value="probleme_mineur">Mineur</option>
            </select>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600">
              <option value="">Statut : tous</option>
              <option value="nouveau">Nouveau</option>
              <option value="en_cours">En cours</option>
              <option value="resolu">Résolu</option>
            </select>
          </div>
        </div>

        {/* ===== Issues list ===== */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
          {issues.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <AlertCircle className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Aucun problème détecté</p>
              <p className="text-sm mt-1">
                {search
                  ? `Aucun résultat pour "${search}"`
                  : 'Cliquez "Analyser les conversations" pour scanner les échanges Beds24'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {issues.map(issue => {
                const isResolved = issue.tracking_status === 'resolu';
                const status = STATUS_LABELS[issue.tracking_status] || STATUS_LABELS.nouveau;
                const source = SOURCE_LABELS[issue.source] || { label: issue.source, color: 'text-slate-500' };
                const incidentDate = issue.incident_date || issue.comment_date || issue.created_at;
                const sevColor = issue.issue_severity === 'probleme_majeur' ? 'bg-[#FF5A6E]'
                  : issue.issue_severity === 'probleme_mineur' ? 'bg-amber-500'
                  : 'bg-emerald-500';
                const sevLabel = issue.issue_severity === 'probleme_majeur' ? 'Problème majeur'
                  : issue.issue_severity === 'probleme_mineur' ? 'Problème mineur'
                  : 'OK';

                return (
                  <Link
                    key={issue.id}
                    to={`/issues/${issue.id}`}
                    className={`group flex items-start gap-4 p-5 hover:bg-slate-50 transition-colors ${isResolved ? 'opacity-60 hover:opacity-100' : ''}`}
                    data-testid={`issue-item-${issue.id}`}
                  >
                    {/* Dot sévérité */}
                    <span
                      title={sevLabel}
                      className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${isResolved ? 'bg-emerald-500' : sevColor}`}
                    />

                    {/* Contenu principal */}
                    <div className="flex-1 min-w-0">
                      {/* Titre */}
                      <h3 className={`text-base font-semibold mb-1 flex items-start gap-2 flex-wrap ${isResolved ? 'text-slate-600 line-through decoration-slate-300' : 'text-slate-900'}`}>
                        <span className="flex-1">{issue.issue_summary || 'Problème détecté'}</span>
                        {issue.recurrence_count > 1 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#FFF0F1] text-[#C73E51] whitespace-nowrap flex-shrink-0">
                            {issue.recurrence_count}×
                          </span>
                        )}
                      </h3>

                      {/* Extrait du commentaire */}
                      {issue.comment_text && (
                        <p className="text-sm text-slate-500 line-clamp-2 mb-2">
                          {issue.comment_text}
                        </p>
                      )}

                      {/* Meta : ligne discrète */}
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 flex-wrap">
                        <Building2 size={11} />
                        <span className="font-medium text-slate-600">{issue.property_name}</span>
                        <span>·</span>
                        <span>{issue.guest_name}</span>
                        {incidentDate && (
                          <>
                            <span>·</span>
                            <span title={exactDate(incidentDate)}>{relativeDate(incidentDate)}</span>
                          </>
                        )}
                        <span className="mx-2 text-slate-300">|</span>
                        <span className={`${status.color} font-medium flex items-center gap-1`}>
                          {isResolved && <CheckCircle2 size={11} />}
                          {status.label}
                        </span>
                        <span>·</span>
                        <span className={source.color}>{source.label}</span>
                        {issue.issue_categories?.length > 0 && (
                          <>
                            <span>·</span>
                            <span className="capitalize">{issue.issue_categories.slice(0, 2).join(', ')}</span>
                            {issue.issue_categories.length > 2 && (
                              <span className="text-slate-400">+{issue.issue_categories.length - 2}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0 mt-2 group-hover:translate-x-1 group-hover:text-[#FF5A6E] transition-all" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default IssuesPage;
